import React from 'react';
import { AppBar, Toolbar, Typography, Box, Chip, Avatar, Button, ButtonGroup, IconButton, Menu, MenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';

const StyledAppBar = styled(AppBar)(({ _theme }) => ({
  backgroundColor: '#ffffff',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
}));

const Logo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const ConnectionChip = styled(Chip)(({ theme, isconnected }) => ({
  backgroundColor: isconnected === 'true' ? 'rgba(76, 201, 240, 0.1)' : 'rgba(249, 65, 68, 0.1)',
  color: isconnected === 'true' ? theme.palette.success.main : theme.palette.error.main,
  '& .MuiChip-avatar': {
    backgroundColor: 'transparent',
    color: isconnected === 'true' ? theme.palette.success.main : theme.palette.error.main,
  },
}));

const NavButton = styled(Button)(({ theme, active }) => ({
  fontWeight: active === 'true' ? 600 : 400,
  borderBottom: active === 'true' ? `2px solid ${theme.palette.primary.main}` : 'none',
  borderRadius: 0,
  padding: theme.spacing(1.5, 2),
}));

const Header = ({ isConnected, currentView, onViewChange, user, onLogout, onOpenLocalServiceConfig, onOpenGeneralSettings }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleUserMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    onLogout();
  };

  return (
    <StyledAppBar position="static">
      <Toolbar>
        <Logo>
          {currentView === 'invoices' ? (
            <ReceiptIcon color="primary" fontSize="large" />
          ) : currentView === 'accounting_examples' ? (
            <AutoAwesomeIcon color="primary" fontSize="large" />
          ) : currentView === 'dian_extractor' ? (
            <CloudUploadIcon color="primary" fontSize="large" />
          ) : currentView === 'third_parties' ? (
            <PeopleIcon color="primary" fontSize="large" />
          ) : (
            <ReceiptIcon color="primary" fontSize="large" />
          )}
          <Typography variant="h5" color="primary" fontWeight={600}>
            {currentView === 'invoices'
              ? 'Seguimiento de Facturas'
              : currentView === 'accounting_examples'
                ? 'Ejemplos Contables (IA)'
              : currentView === 'fiscal_config'
                ? 'Configuración Fiscal'
              : currentView === 'third_parties'
                ? 'Gestión de Terceros'
              : currentView === 'dian_extractor'
                ? 'Extractor DIAN'
              : 'Seguimiento de Facturas'}
          </Typography>
        </Logo>

        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
          <ButtonGroup variant="text" color="primary">
            <NavButton
              active={currentView === 'invoices' ? 'true' : 'false'}
              onClick={() => onViewChange('invoices')}
              startIcon={<ReceiptIcon />}
            >
              Facturas
            </NavButton>
            <NavButton
              active={currentView === 'accounting_examples' ? 'true' : 'false'}
              onClick={() => onViewChange('accounting_examples')}
              startIcon={<AutoAwesomeIcon />}
            >
              Ejemplos IA
            </NavButton>
            <NavButton
              active={currentView === 'fiscal_config' ? 'true' : 'false'}
              onClick={() => onViewChange('fiscal_config')}
              startIcon={<AccountBalanceIcon />}
            >
              Configuración Fiscal
            </NavButton>
            <NavButton
              active={currentView === 'third_parties' ? 'true' : 'false'}
              onClick={() => onViewChange('third_parties')}
              startIcon={<PeopleIcon />}
            >
              Terceros
            </NavButton>
            <NavButton
              active={currentView === 'dian_extractor' ? 'true' : 'false'}
              onClick={() => onViewChange('dian_extractor')}
              startIcon={<CloudUploadIcon />}
            >
              Extractor DIAN
            </NavButton>
          </ButtonGroup>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ConnectionChip
            isconnected={isConnected ? 'true' : 'false'}
            avatar={<Avatar><FiberManualRecordIcon fontSize="small" /></Avatar>}
            label={isConnected ? 'Conectado' : 'Desconectado'}
            variant="outlined"
          />
          
          {user && (
            <>
              <IconButton
                onClick={handleUserMenuClick}
                size="small"
                sx={{ ml: 2 }}
                aria-controls={open ? 'user-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
              >
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <AccountCircleIcon />
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                id="user-menu"
                open={open}
                onClose={handleUserMenuClose}
                onClick={handleUserMenuClose}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 1.5,
                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={handleUserMenuClose} disabled>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <AccountCircleIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2">{user.first_name} {user.last_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                  </Box>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleUserMenuClose();
                    if (onOpenLocalServiceConfig) onOpenLocalServiceConfig();
                  }}
                >
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <AccountCircleIcon fontSize="small" />
                  </Avatar>
                  Configuración de servicio local
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleUserMenuClose();
                    if (onOpenGeneralSettings) onOpenGeneralSettings();
                  }}
                >
                  <Avatar sx={{ bgcolor: 'secondary.main' }}>
                    <SettingsIcon fontSize="small" />
                  </Avatar>
                  Configuraciones Generales
                </MenuItem>
                {!user.is_sub_user && (
                  <MenuItem onClick={() => { handleUserMenuClose(); onViewChange('sub_users'); }}>
                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                      <PeopleIcon fontSize="small" />
                    </Avatar>
                    Sub Usuarios
                  </MenuItem>
                )}
                <MenuItem onClick={handleLogout}>
                  <Avatar sx={{ bgcolor: 'error.main' }}>
                    <LogoutIcon fontSize="small" />
                  </Avatar>
                  Cerrar Sesión
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
};

export default Header;